"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MassachusettsRules = exports.MA_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const isNonEmpty_1 = require("encamp-shared/dist/utils/isNonEmpty");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * Massachusetts (MassDEP) accumulation limits, 310 CMR 30.000.
 *
 * Massachusetts runs its own generator tiers alongside the federal ones, so
 * the clocks key on the stricter of the two axes (see `effectiveStatus`).
 *
 * ── KNOWN GAPS
 *
 * 1. WASTE OIL generator status - see governingProgram.
 * 2. VSQG acute ban.
 * 3. Regulated recyclable material.
 * 4. Acute mass cap, VSQG per-stream allowance, full containers.
 */
const MA_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR",
    "SAME_AS_FEDERAL",
];
/**
 * "Same as Federal" is the Massachusetts record deferring to the federal
 * axis, so it resolves to the Massachusetts tier that federal status names
 * before the stricter-of comparison runs.
 */
const MA_TIER_BY_FEDERAL_STATUS = {
    LQG: "LARGE_QUANTITY_GENERATOR",
    SQG: "SMALL_QUANTITY_GENERATOR",
    VSQG: "VERY_SMALL_QUANTITY_GENERATOR",
};
const MA_SATELLITE_SOURCE = {
    jurisdiction: "MA",
    citation: "310 CMR 30.340(6), 30.353(6)(i)",
};
/** Federal rules under Massachusetts' regulation. */
exports.MA_THRESHOLDS = {
    centralTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: { jurisdiction: "MA", citation: "310 CMR 30.340(4), 30.351(5), 30.353" },
    },
    capNonAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capNonAcuteVolume, source: MA_SATELLITE_SOURCE },
    capAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteVolume, source: MA_SATELLITE_SOURCE },
    capAcuteMass: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteMass, source: MA_SATELLITE_SOURCE },
    satelliteToCentralMoveWindow: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow,
        source: MA_SATELLITE_SOURCE,
    },
    onSiteTotal: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteTotal,
        source: { jurisdiction: "MA", citation: "310 CMR 30.351(1)(b)" },
    },
    onSiteNonAcute: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteNonAcute,
        source: { jurisdiction: "MA", citation: "310 CMR 30.353(1)(b), (3)" },
    },
    onSiteAcute: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteAcute,
        source: { jurisdiction: "MA", citation: "310 CMR 30.353(1)(c), 30.351(7)" },
    },
};
/** Massachusetts (MassDEP) accumulation limits, 310 CMR 30.000. */
class MassachusettsRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = MA_STATE_STATUS_DOMAIN;
        this.adoptsEpisodicEvents = false;
    }
    /** The effective Massachusetts tier, named in the federal vocabulary. */
    describeGeneratorStatus(facility) {
        const effective = this.effectiveStatus(facility);
        return effective.value === undefined
            ? null
            : _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES[effective.value];
    }
    /**
     * 180 days flat for an SQG: Massachusetts adopted no counterpart to
     * 262.16(c)'s 270 days. No time limit for a VSQG, which 30.353 constrains by
     * quantity instead.
     */
    centralTimeLimit(situation) {
        const effective = this.effectiveStatus(situation.facility);
        const basis = this.statusBasis(situation.facility);
        if (effective.value === undefined) {
            return {
                ...exports.MA_THRESHOLDS.centralTimeLimit,
                determination: "UNDETERMINED",
                missing: effective.missing,
                basis,
            };
        }
        switch (effective.value) {
            case "LQG":
            case "SQG": {
                const source = {
                    jurisdiction: "MA",
                    citation: effective.value === "LQG" ? "310 CMR 30.340(4)" : "310 CMR 30.351(5)",
                };
                return {
                    ...exports.MA_THRESHOLDS.centralTimeLimit,
                    source,
                    determination: "KNOWN",
                    basis,
                    value: effective.value === "LQG" ? 90 : 180,
                };
            }
            case "VSQG":
                return {
                    ...exports.MA_THRESHOLDS.centralTimeLimit,
                    source: { jurisdiction: "MA", citation: "310 CMR 30.353" },
                    determination: "EXEMPT",
                    basis,
                };
        }
    }
    /** Massachusetts never adopted subpart L, so no container takes the episodic window. */
    containerThresholds(situation) {
        const program = this.governingProgram(situation.container);
        // A satellite container's move window reports a missing program itself.
        if (program.value === undefined && situation.area.type !== "SATELLITE") {
            const missing = [
                ...program.missing,
                ...(situation.area.type === undefined ? ["area.type"] : []),
            ];
            return [
                { ...exports.MA_THRESHOLDS.centralTimeLimit, determination: "UNDETERMINED", missing, basis: {} },
            ];
        }
        if (program.value === "UNIVERSAL_WASTE") {
            return [{ ..._FEDERAL_1.FEDERAL_THRESHOLDS.universalWasteTimeLimit, determination: "KNOWN" }];
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [this.centralTimeLimit(situation)];
            case "SATELLITE": {
                const caps = this.satelliteCaps();
                return [
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, exports.MA_THRESHOLDS.satelliteToCentralMoveWindow, caps, situation.container),
                ];
            }
            case undefined:
                return [
                    {
                        ...exports.MA_THRESHOLDS.centralTimeLimit,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * The stricter of the federal status and the Massachusetts tier
     * (310 CMR 30.340, 30.351, 30.353). LQG on either settles it; otherwise
     * both must be known.
     */
    effectiveStatus(facility) {
        const federal = facility.federalGeneratorStatus;
        const state = (0, _stateRules_1.narrowStateStatus)(facility, MA_STATE_STATUS_DOMAIN);
        const tier = state === "SAME_AS_FEDERAL"
            ? federal === undefined
                ? undefined
                : MA_TIER_BY_FEDERAL_STATUS[federal]
            : state;
        if (federal === "LQG" || tier === "LARGE_QUANTITY_GENERATOR") {
            return { value: "LQG" };
        }
        const missing = [
            ...(federal === undefined ? ["facility.federalGeneratorStatus"] : []),
            ...(state === undefined ? ["facility.stateGeneratorStatus"] : []),
        ];
        if ((0, isNonEmpty_1.isNonEmpty)(missing)) {
            return { missing };
        }
        if (federal === "SQG" || tier === "SMALL_QUANTITY_GENERATOR") {
            return { value: "SQG" };
        }
        return { value: "VSQG" };
    }
    /** The generator statuses the thresholds read, as supplied. */
    statusBasis(facility) {
        const federal = facility.federalGeneratorStatus;
        const state = (0, _stateRules_1.narrowStateStatus)(facility, MA_STATE_STATUS_DOMAIN);
        if (federal === undefined && state === undefined) {
            return {};
        }
        return {
            facility: {
                ...(federal === undefined ? {} : { federalGeneratorStatus: federal }),
                ...(state === undefined ? {} : { stateGeneratorStatus: state }),
            },
        };
    }
    /**
     * Universal waste in Massachusetts is the federal program (310 CMR 30.1000
     * restates the one-year limit). Pharmaceutical waste is ordinary waste:
     * Massachusetts never adopted subpart P. So is used oil, but KNOWN GAP:
     * 310 CMR 30.253 gives used oil its own generator status, which this
     * assumes matches the hazardous waste one.
     */
    governingProgram(container) {
        if (container.wasteKind === undefined) {
            return { missing: ["container.wasteKind"] };
        }
        return { value: container.wasteKind === "UNIVERSAL_WASTE" ? "UNIVERSAL_WASTE" : "RCRA" };
    }
    /**
     * Federal figures under Massachusetts' own regulation, with no VSQG
     * carve-out: 30.353(6)(i) gives a VSQG its own satellite allowance. The
     * acute mass figure is the federal stand-in for 30.340(6)(c)(2)'s quart-only
     * limit, over-strict and never under-strict. They apply simultaneously;
     * crossing one starts its move window.
     */
    satelliteCaps() {
        return [
            {
                ...exports.MA_THRESHOLDS.capNonAcuteVolume,
                determination: "KNOWN",
                basis: {},
            },
            {
                ...exports.MA_THRESHOLDS.capAcuteVolume,
                determination: "KNOWN",
                basis: {},
            },
            {
                ...exports.MA_THRESHOLDS.capAcuteMass,
                determination: "KNOWN",
                basis: {},
            },
        ];
    }
    areaThresholds(situation) {
        switch (situation.area.type) {
            case "CENTRAL":
                return [];
            case "SATELLITE":
                return this.satelliteCaps();
            case undefined:
                return [
                    {
                        ...exports.MA_THRESHOLDS.capNonAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.MA_THRESHOLDS.capAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.MA_THRESHOLDS.capAcuteMass,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * Massachusetts' figures are the federal ones (30.351(1)(b), 30.353(1)(b)),
     * keyed on the effective Massachusetts tier. Universal waste counts toward
     * none of them (30.351(2)(b)4, 30.353(2)(b)4). KNOWN GAP: waste oil counts,
     * though 30.253(5) lets a generator count it toward a separate waste oil
     * status instead.
     */
    facilityThresholds(facility) {
        const effective = this.effectiveStatus(facility);
        const basis = this.statusBasis(facility);
        if (effective.value === undefined) {
            return [
                {
                    ...exports.MA_THRESHOLDS.onSiteTotal,
                    source: { jurisdiction: "MA", citation: "310 CMR 30.351(1)(b), 30.353(1)(b)" },
                    determination: "UNDETERMINED",
                    missing: effective.missing,
                    basis,
                    consequences: [{ kind: "COMPLIANCE_NOT_RECORDED" }],
                },
            ];
        }
        switch (effective.value) {
            case "LQG":
                return [];
            case "SQG":
                return [
                    {
                        ...exports.MA_THRESHOLDS.onSiteTotal,
                        determination: "KNOWN",
                        basis,
                        value: wasteGeneratorStatusThresholds_1.SQG_ACCUMULATION_THRESHOLD_KG,
                        consequences: [
                            {
                                kind: "GENERATOR_STATUS_OUTCOME_NOT_RECORDED",
                                to: _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES.LQG,
                            },
                        ],
                    },
                ];
            case "VSQG":
                return [
                    {
                        ...exports.MA_THRESHOLDS.onSiteNonAcute,
                        determination: "KNOWN",
                        basis,
                        value: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_KG,
                        // 30.353(3) gives an SQG or LQG status "as the case may be"; SQG is
                        // the first one a VSQG reaches.
                        consequences: [
                            {
                                kind: "GENERATOR_STATUS_OUTCOME_NOT_RECORDED",
                                to: _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES.SQG,
                            },
                        ],
                    },
                    {
                        ...exports.MA_THRESHOLDS.onSiteAcute,
                        determination: "KNOWN",
                        basis,
                        value: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_ACUTE_KG,
                        consequences: [
                            {
                                kind: "GENERATOR_STATUS_OUTCOME_NOT_RECORDED",
                                to: _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES.LQG,
                            },
                        ],
                    },
                ];
        }
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
}
exports.MassachusettsRules = MassachusettsRules;
//# sourceMappingURL=MA.js.map